import styled from '@emotion/styled';
import { ProjectEvaluateNumCardNumberSection } from 'components/molecules/projectEvaluateNumCard';
import FadeUpItem from 'components/organisms/fadeUpItem';
import React from 'react';
import fonts from 'style/fonts';
import { text } from 'style/mixin';
import { baseTransitionSecond, mq } from 'style/variables';
import { useIsSp } from 'util/useIsSp';

type TopFactType = '0' | '1' | '2' | '3' | '4' | '5';

interface TopFactProps {
  mainImage: React.ReactNode;
  title: React.ReactNode;
  counts: number;
  unitPre?: string;
  unit?: React.ReactNode;
  subImage1: string;
  subImage2?: string;
  subImage1Count: number;
  subImage2Count: number;
  type: TopFactType;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 16px;
  margin-bottom: 98px;
  white-space: nowrap;
  color: #1a1311;

  ${mq.onlysp} {
    align-items: center;
    width: 100%;
    margin-bottom: 22px;
    gap: 14px;

    &:nth-of-type(2n) {
      margin-bottom: 64px;
    }
  }
  .main-image {
    width: 176px;
    height: 176px;

    ${mq.onlysp} {
      width: 180px;
      height: 180px;
    }
  }
  .sub-images {
    display: flex;
    ${mq.onlysp} {
      display: none;
    }
  }
  .sub-image-1 {
    display: grid;
    align-items: end;
    width: calc(53px * 2);
    div {
      width: 53px;
    }
    img {
      width: 100%;
    }
  }
  .sub-image-2 {
    display: grid;

    div {
      width: 25px;
    }

    img {
      width: 100%;
    }
  }

  // .unit-plus の英語版・タイ語版スタイル（SP/PC共通）
  &.type-4 {
    .unit-plus {
      [data-lang='en'] & {
        right: -17px;
      }
      [data-lang='th'] & {
        right: -17px;
      }
    }
  }

  ${mq.onlypc} {
    // レイアウト幅の設定
    &.type-0,
    &.type-1,
    &.type-2,
    &.type-3 {
      width: 25%;
    }

    &.type-4,
    &.type-5 {
      width: 50%;
      flex-direction: row-reverse;

      .fact-body {
        display: flex;
        flex-direction: column-reverse;
      }
    }

    // 共通のレイアウト設定
    &.type-1,
    &.type-3 {
      flex-direction: column-reverse;
      align-items: end;
      justify-content: flex-end;
    }

    // Type 0: 左上配置
    &.type-0 {
      .main-image {
        margin-left: 6px;
      }
      .title-count {
        padding-left: 9px;
      }
      .sub-images {
        gap: 2px;
        margin-top: -2px;
      }
      .sub-image-1 {
        grid-template-columns: repeat(4, 1fr);
        grid-template-rows: repeat(1, 1fr);
        width: calc(52px * 4 + 2px * 3);
        margin-left: 2px;

        > div {
          margin-right: -2px;
        }
      }
      .sub-image-2 {
        grid-template-columns: repeat(8, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: calc(30px * 10);
        height: auto;
      }
    }

    // Type 1: 右上配置
    &.type-1 {
      padding-right: 6px;
      .title-count {
        // TODO: 後でなんとかしたい
        div > div > div > div:has(.unit-plus) {
          margin-left: 6px;
          padding-left: 3px;
          padding-bottom: 11px;
        }

        p {
          margin-left: 18px;
        }
      }

      .main-image {
        padding-left: 44px;
        margin-top: 6px;
      }

      .sub-images {
        flex-direction: row-reverse;
        align-items: center;
        gap: 12px;
        margin-right: -38px;
        margin-top: -16px;
      }

      .sub-image-1 {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(1, 1fr);
        column-gap: 10px;
        width: calc(56px * 2 + 10px * 1);

        div {
          width: 56px;
        }
      }

      .sub-image-2 {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(1, 1fr);
        column-gap: 4px;
        justify-items: end;
        width: calc(34px * 2 + 4px);
        height: auto;

        div {
          width: 34px;
        }
      }
    }

    // Type 2: 左下配置
    &.type-2 {
      padding-left: 97px;

      .main-image {
        margin-top: -4px;
      }

      .fact-body {
        margin-left: 3px;
        margin-top: 6px;
      }

      .title-count {
        margin-top: -3px;
      }

      .sub-images {
        flex-direction: column;
        margin-left: -4px;
      }

      .sub-image-1 {
        grid-template-columns: repeat(5, 1fr);
        grid-template-rows: repeat(1, 1fr);
        column-gap: 1px;
        width: calc(44px * 5 + 1px * 4);
        margin-top: -9px;

        div {
          width: 44px;
        }
      }

      .sub-image-2 {
        grid-template-columns: repeat(6, 1fr);
        grid-template-rows: repeat(1, 1fr);
        column-gap: 1px;
        width: calc(30px * 6 + 1px * 5);
        height: auto;

        div {
          width: 30px;
        }
      }
    }

    // Type 3: 右下配置
    &.type-3 {
      padding-right: 24px;

      .main-image {
        margin-right: -18px;
        margin-top: 5px;
      }

      .sub-images {
        flex-direction: column;
        margin-right: -6px;
        margin-top: -4px;
      }

      .sub-image-1 {
        grid-template-columns: repeat(4, 1fr);
        grid-template-rows: repeat(1, 1fr);
        column-gap: 4px;
        width: calc(46px * 4 + 4px * 3);
        margin-left: auto;
        div {
          width: 46px;
        }
      }

      .sub-image-2 {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(1, 1fr);
        column-gap: 3px;
        width: calc(22px * 3);
        height: auto;
        div {
          width: 22px;
        }
      }
    }

    // Type 4: 下部左側（大きなレイアウト）
    &.type-4 {
      align-items: flex-start;
      justify-content: flex-end;
      padding-left: 2px;

      .main-image {
        position: absolute;
        bottom: 19px;
        left: 228px;
        width: 208px;
        height: 208px;
      }

      .title-count {
        margin-left: -6px;
      }

      .fact-body {
        gap: 13px;
      }

      .sub-images {
        flex-direction: column-reverse;
        margin-left: -7px;
      }

      .sub-image-1 {
        grid-template-columns: repeat(4, 1fr);
        grid-template-rows: repeat(2, 1fr);
        column-gap: 7px;
        row-gap: 6px;
        width: calc(50px * 4 + 4px * 3);
        margin-top: 6px;

        div {
          width: 50px;
        }
      }

      .sub-image-2 {
        grid-template-columns: repeat(5, 1fr);
        grid-template-rows: repeat(1, 1fr);
        column-gap: 8px;
        width: calc(36px * 5 + 8px * 4);
        height: auto;

        div {
          width: 36px;
        }
      }

      .unit-plus {
        bottom: 76px;
        right: -17px;
      }
    }

    // Type 5: 下部右側（大きなレイアウト）
    &.type-5 {
      align-items: flex-start;
      gap: 104px;
      .main-image {
        width: 203px;
        height: 208px;
        margin-top: -12px;
        margin-right: 38px;
      }
      .sub-images {
        flex-direction: row;
        gap: 5px;
        margin-top: -3px;
      }
      .fact-body {
        padding-top: 4px;
      }
      .title-count {
        position: absolute;
        bottom: 4px;
        right: 65px;
        width: 334px;
      }

      .sub-image-1 {
        grid-template-columns: repeat(6, 1fr);
        grid-template-rows: repeat(3, 1fr);
        column-gap: 6px;
        row-gap: 16px;
        width: calc(56px * 6 + 6px * 5);

        div {
          width: 56px;
          &:nth-of-type(12) {
            div {
              display: none;
            }
          }
        }
      }

      .sub-image-2 {
        // grid-template-columns: repeat(4, 1fr);
        // grid-template-rows: repeat(2, 1fr);
        // column-gap: 4px;
        // row-gap: 6px;
        // width: calc((46px + 4px) * 4);
        // height: fit-content;
        // div {
        //   width: 46px;
        // }
      }
      .unit-alphabet {
        font-size: 78px;
      }

      .unit-plus {
        bottom: 77px;
        right: 25px;
      }
    }
  }
`;

const Title = styled.p`
  font-family: 'toppan-bunkyu-midashi-go-std', 'Noto Sans Thai', sans-serif;
  ${text(18, 1.2)}
  font-weight: 900;
  letter-spacing: 0.15em;
  margin-bottom: 12px;

  .type-3 & {
    letter-spacing: 0.1em;

    ${mq.onlypc} {
      [data-lang='en'] & {
        text-align: right;
      }
      [data-lang='th'] & {
        text-align: right;
      }
    }
  }
  [data-lang='en'] & {
    font-family: ${fonts.enGothic};
    letter-spacing: 0em;
    margin-bottom: 8px;
  }
  [data-lang='th'] & {
    font-family: ${fonts.thSans};
    letter-spacing: 0em;
    margin-bottom: 8px;
  }
  ${mq.onlysp} {
    [data-lang='en'] & {
      text-align: center;
    }
    [data-lang='th'] & {
      text-align: center;
    }
  }
`;

const Counts = styled.div`
  display: flex;
  font-size: 96px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -1.92px;
  margin-bottom: 6px;

  ${mq.onlysp} {
    [data-lang='en'] & {
      justify-content: center;
    }
  }
  // カウント時のガクガクを防止
  ${mq.onlypc} {
    .type-1 & {
      width: 374px;
      [data-lang='en'] & {
        width: 362px;
      }
      [data-lang='th'] & {
        width: 362px;
      }
    }
    .type-3 & {
      width: 250px;
      [data-lang='en'] & {
        width: 264px;
      }
      [data-lang='th'] & {
        width: 264px;
      }
    }
  }
`;

const UnitPre = styled.span`
  font-family: 'toppan-bunkyu-midashi-go-std', 'Noto Sans Thai', sans-serif;
  font-size: 34px;
  font-weight: 900;
  line-height: 109%; /* 37.06px */
  letter-spacing: 3.64px;
  text-align: end;
  writing-mode: vertical-rl;
  padding-bottom: 6px;
  padding-right: 10px;

  ${mq.onlysp} {
    padding-right: 2px;
    padding-bottom: 0;
  }
`;

export const TopFact: React.FC<TopFactProps> = ({
  mainImage,
  title,
  counts,
  unitPre,
  unit,
  subImage1,
  subImage2,
  subImage1Count,
  subImage2Count,
  type,
}) => {
  const isSP = useIsSp();
  const _subImage2 = subImage2 ? subImage2 : '';
  const baseDelay = isSP ? 0 : (Number(type) % 2) * 0.5 - 0.5;
  const delayMainImage = isSP ? 0.5 : 1.9 + baseDelay;
  const delayImages = 0.9 + baseDelay;
  const delayCountUp = isSP ? 0.5 : 0.9 + baseDelay;
  const subImageDelayRatio = 2.5;
  return (
    <Container className={`type-${type}`}>
      <div className="main-image">
        <FadeUpItem delay={delayMainImage + baseTransitionSecond / 3} invokeInView extraDuration={0.2}>
          {mainImage}
        </FadeUpItem>
      </div>

      <div className="fact-body">
        <div className="title-count">
          <FadeUpItem delay={delayCountUp} invokeInView>
            <Title>{title}</Title>
            <Counts>
              {unitPre && <UnitPre>{unitPre}</UnitPre>}

              <ProjectEvaluateNumCardNumberSection
                size={['4', '5'].includes(type) ? 'xlarge' : 'large'}
                number={counts.toString()}
                unit={(unit || '') as string}
                delay={delayCountUp}
              />
            </Counts>
          </FadeUpItem>
        </div>
        <div className="sub-images">
          <div className="sub-image-1">
            {Array.from({ length: subImage1Count }).map((_, index) => (
              <div key={index}>
                <FadeUpItem
                  delay={delayImages + (index * baseTransitionSecond) / subImageDelayRatio}
                  invokeInView
                  fadeScaleUp
                >
                  <img src={subImage1} alt="" />
                </FadeUpItem>
              </div>
            ))}
          </div>
          <div className="sub-image-2">
            {Array.from({ length: subImage2Count }).map((_, index) => (
              <div key={index}>
                <FadeUpItem
                  delay={
                    baseDelay + delayImages + ((index + subImage1Count) * baseTransitionSecond) / subImageDelayRatio
                  }
                  invokeInView
                  fadeScaleUp
                >
                  <img src={_subImage2} alt="" />
                </FadeUpItem>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Container>
  );
};

export const TopFactOrganizer = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;
